import DOMPurify from 'dompurify';

const allowedEmailTags = [
  'p',
  'br',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'a',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'img',
  'table',
  'tbody',
  'tr',
  'td',
  'th',
  'style',
];

const allowedEmailAttributes = [
  'href',
  'src',
  'alt',
  'title',
  'style',
  'class',
  'target',
  'width',
  'height',
  // Table layout/presentational attributes used by HTML emails. Without these,
  // DOMPurify strips them and the preview no longer matches the sent email — e.g.
  // a stripped `colspan` collapses the line-item total row so the grand total is
  // no longer aligned under the price column.
  'colspan',
  'rowspan',
  'align',
  'valign',
  'bgcolor',
  'cellpadding',
  'cellspacing',
  'border',
];

// Class of the container a mail body is rendered into; also the scope a mail's
// own stylesheet is confined to. The matching look lives in src/index.css.
export const MAIL_BODY_CLASS = 'email-content';

const SCOPE_SELECTOR = `.${MAIL_BODY_CLASS}`;

// The selectors a mail uses for the document it believes it owns.
const DOCUMENT_ROOT_PATTERN = /^(?:html|body|:root)\b/i;

// Split a selector list on its top-level commas. Commas inside :is(…), an
// attribute selector or a string belong to the selector, not to the list.
const splitSelectorList = selectorText => {
  const parts = [];
  let current = '';
  let depth = 0;
  let quote = null;

  for (const char of selectorText) {
    if (quote) {
      current += char;
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      current += char;
      continue;
    }
    if (char === '(' || char === '[') depth += 1;
    if (char === ')' || char === ']') depth -= 1;
    if (char === ',' && depth <= 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts;
};

// Confine a single selector to the mail body. A selector for the document root
// becomes the container itself, so a mail that styles `body` styles its own
// sheet instead of the dashboard.
const scopeSelector = selector => {
  const trimmed = selector.trim();
  if (!trimmed) return '';
  if (DOCUMENT_ROOT_PATTERN.test(trimmed)) {
    const rest = trimmed.replace(DOCUMENT_ROOT_PATTERN, '').trim();
    if (!rest) return SCOPE_SELECTOR;
    // `body.promo` compounds onto the container, `body p` becomes a descendant.
    return /^[.#[:]/.test(rest) ? `${SCOPE_SELECTOR}${rest}` : `${SCOPE_SELECTOR} ${rest}`;
  }
  return `${SCOPE_SELECTOR} ${trimmed}`;
};

const scopeSelectorList = selectorText =>
  splitSelectorList(selectorText).map(scopeSelector).filter(Boolean).join(',');

// Write the rules back out with every selector scoped. Serialising through the
// CSSOM is what makes this safe: the browser has already parsed the mail's CSS,
// so no stray brace in the source can close the scope and escape it. At-rules
// that cannot be confined to a subtree — @import, @font-face, @keyframes,
// @page — are dropped, because each would take effect document-wide again.
const serializeScopedRules = rules => {
  let css = '';
  for (const rule of Array.from(rules)) {
    if (rule instanceof CSSStyleRule) {
      const selector = scopeSelectorList(rule.selectorText);
      const declarations = rule.style.cssText;
      if (selector && declarations) css += `${selector}{${declarations}}`;
    } else if (rule instanceof CSSMediaRule || rule instanceof CSSSupportsRule) {
      const inner = serializeScopedRules(rule.cssRules);
      if (!inner) continue;
      const keyword = rule instanceof CSSMediaRule ? 'media' : 'supports';
      css += `@${keyword} ${rule.conditionText}{${inner}}`;
    }
  }
  return css;
};

// Rewrite a mail's stylesheet so its rules only reach the mail body. A <style>
// that sits inside the body of a mail — the shape every quoted reply has —
// survives sanitising and would otherwise apply to the whole dashboard.
export const scopeMailCss = css => {
  const source = String(css == null ? '' : css).trim();
  if (!source) return '';

  // A document without a browsing context: the stylesheet is parsed, but
  // nothing in it can affect the page while it is read back.
  const inertDocument = document.implementation.createHTMLDocument('');
  const styleElement = inertDocument.createElement('style');
  styleElement.textContent = source;
  inertDocument.head.appendChild(styleElement);

  const sheet = styleElement.sheet;
  if (!sheet) return '';
  try {
    return serializeScopedRules(sheet.cssRules);
  } catch (error) {
    // An unreadable stylesheet is dropped instead of being passed through.
    return '';
  }
};

// Confine every stylesheet the sanitised markup still carries to the mail body.
const scopeStyleTags = html => {
  if (!html || html.indexOf('<style') === -1) return html;

  // A template's content is inert: parsing here neither loads resources nor
  // applies the styles that are about to be rewritten.
  const template = document.createElement('template');
  template.innerHTML = html;
  for (const styleElement of Array.from(template.content.querySelectorAll('style'))) {
    const scoped = scopeMailCss(styleElement.textContent || '');
    if (scoped) styleElement.textContent = scoped;
    else styleElement.remove();
  }
  return template.innerHTML;
};

const hasTextContent = value => typeof value === 'string' && value.trim().length > 0;

const escapePlainText = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\r\n|\r|\n/g, '<br>');

export const sanitizeEmailHtml = htmlContent => {
  if (!hasTextContent(htmlContent)) return '';

  return scopeStyleTags(
    DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: allowedEmailTags,
      ALLOWED_ATTR: allowedEmailAttributes,
      ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      KEEP_CONTENT: true,
      FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange'],
    })
  );
};

export const getRenderableEmailHtml = mail => {
  const htmlContent = mail?.htmlContent || mail?.htmlBody;
  if (hasTextContent(htmlContent)) {
    return sanitizeEmailHtml(htmlContent);
  }

  const textContent = mail?.plainContent || mail?.textBody;
  if (hasTextContent(textContent)) {
    return escapePlainText(textContent);
  }

  return '';
};

export const hasRenderableEmailContent = mail =>
  hasTextContent(mail?.htmlContent) ||
  hasTextContent(mail?.htmlBody) ||
  hasTextContent(mail?.plainContent) ||
  hasTextContent(mail?.textBody);
